import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '../Store';
import axios from 'axios';
import qs from "qs";
import {Storage} from "../../Utils/StorageUtils";
import {AUTHENTICATION_URL} from "../Constants";
import { loadUserConfiguration } from './ConfigurationReducer';
import { generateCodeChallenge, generateRandomString } from '../../Utils/pkceUtils';
import { AuthContextProps } from 'react-oidc-context';
import { clearStoredConversationId } from '../../Modules/AIAgent/services/AIAgentService';
import {
    beginSessionGeneration,
    isActiveSessionGeneration,
    resetSessionGenerationForTests,
} from '../Session/sessionInitialization';
const RESET_NAVIGATION_MENU_ACTION = { type: 'dashboardNavigationMenuReducer/reset' };

const AUTH_TOKEN_KEY = 'api-token-key';
const USER_MAIL = 'user-mail';
const USER_NAME = 'user-name';
export class UserEntity {
    username: string | undefined;
    password: string | undefined;
    rememberMe: boolean | undefined;
}

export class Authority {
    id: number | null | undefined;
    name: string | null | undefined;
    displayName: string | null | undefined;
}

export class AccountEntity {
    id: number | null | undefined;
    name: string | null | undefined;
    userName: string | null | undefined;
    customerName: string | null | undefined;
    authoritySet: Array<Authority> | null | undefined;
    email: string | null;
    homeScreen: string | null | undefined;

    constructor() {
        this.id = null;
        this.name = null;
        this.authoritySet = null;
        this.email = null;
        this.customerName = null;
        this.homeScreen = null;
    }
}

export interface DefaultAuthState {
    loading: boolean;
    isAuthenticated: boolean;
    loginSuccess: boolean;
    loginError: boolean;
    showModalLogin: boolean;
    account: AccountEntity | undefined;
    errorMessage: string | undefined;
    redirectMessage: string | undefined;
    sessionHasBeenFetched: boolean;
    idToken: string | undefined;
    logoutUrl: string | undefined;
    customer: []
}

const initialState: DefaultAuthState = {
    loading: false,
    isAuthenticated: false,
    loginSuccess: false,
    loginError: false,
    showModalLogin: false,
    account: undefined,
    errorMessage: undefined, // Errors returned from server side
    redirectMessage: undefined,
    sessionHasBeenFetched: false,
    idToken: undefined,
    logoutUrl: undefined,
    customer: []
};

// Actions

export type GetAccountArg = { generationId: number };

export const getAccount = createAsyncThunk(
    'authentication/get_account',
    async ({ generationId }: GetAccountArg) => {
        const response = await axios.get('api/account');
        return { generationId, response };
    }
);

export const getCustomer = createAsyncThunk(
    'authentication/get_customer',
    async () => axios.get('api/customer/all'),
)

export const authenticate = createAsyncThunk(
    'authentication/authenticate',
    async (auth: UserEntity) => {
        const codeVerifier = generateRandomString(96);
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            data: qs.stringify({
                'client_id': 'sgd-web-client',
                'grant_type': 'password',
                'username': auth.username,
                'password': auth.password,  
                'response_type': 'code',
                'code_challenge': codeChallenge,
                'code_challenge_method': 'S256',
            }),
            url: AUTHENTICATION_URL
        };
        return axios(options);
    },
);

type LoginFlight = {
    token: string;
    generationId: number;
    promise: Promise<void>;
};

let loginFlight: LoginFlight | null = null;

export function resetLoginFlightForTests(): void {
    loginFlight = null;
    resetSessionGenerationForTests();
}

const installAuthCredentials = (auth: AuthContextProps, bearerToken: string) => {
    Storage.local.set(AUTH_TOKEN_KEY, bearerToken);
    Storage.session.set(AUTH_TOKEN_KEY, bearerToken);
    if (auth.user?.profile?.preferred_username) {
        Storage.local.set(USER_NAME, auth.user.profile.preferred_username);
    }
    if (auth.user?.profile?.email) {
        Storage.local.set(USER_MAIL, auth.user.profile.email);
        Storage.session.set(USER_MAIL, auth.user.profile.email);
    }
};

export const login = (auth: AuthContextProps, options?: { generationId?: number }) => {
    return async function (dispatch: any) {
        const bearerToken = auth.user?.access_token;
        if (!bearerToken || !auth?.isAuthenticated) {
            return;
        }

        if (loginFlight?.token === bearerToken) {
            return loginFlight.promise;
        }

        const generationId = options?.generationId ?? beginSessionGeneration();

        const promise = (async () => {
            try {
                installAuthCredentials(auth, bearerToken);
                dispatch(invalidateAuthenticatedContext());
                dispatch(RESET_NAVIGATION_MENU_ACTION);

                await dispatch(loadUserConfiguration());
                const accountResult = await dispatch(getAccount({ generationId }));

                if (!isActiveSessionGeneration(generationId)) {
                    return;
                }

                if (getAccount.rejected.match(accountResult)) {
                    throw accountResult.error ?? new Error('account_load_failed');
                }
            } finally {
                if (loginFlight?.generationId === generationId) {
                    loginFlight = null;
                }
            }
        })();

        loginFlight = { token: bearerToken, generationId, promise };
        return promise;
    };
};

export const clearAuthToken = () => {
    if (Storage.local.get(AUTH_TOKEN_KEY)) {
        Storage.local.remove(AUTH_TOKEN_KEY);
    }
    if (Storage.session.get(AUTH_TOKEN_KEY)) {
        Storage.session.remove(AUTH_TOKEN_KEY);
    }
};

const clearUnauthenticatedState = (dispatch: any) => {
    clearAuthToken();
    dispatch(logoutSession());
};

export const synchronizeUnauthenticatedState = () => (dispatch: any) => {
    clearUnauthenticatedState(dispatch);
};

export const logout = () => (dispatch: any) => {
    clearStoredConversationId();
    clearUnauthenticatedState(dispatch);
    // O logout do Keycloak será feito no componente que chama esta ação
};

export const clearAuthentication = (messageKey: any) => (dispatch: any) => {
    dispatch(authError(messageKey));
    dispatch(clearAuth());
};

// Slices
export const authenticationReducer = createSlice({
    name: 'authentication',
    initialState: initialState,
    reducers: {
        logoutSession() {
            return {
                ...initialState,
                showModalLogin: true,
            };
        },
        authError(state, action) {
            return {
                ...state,
                showModalLogin: true,
                redirectMessage: action.payload,
            };
        },
        clearAuth(state) {
            return {
                ...state,
                loading: false,
                showModalLogin: true,
                isAuthenticated: false,
            };
        },
        invalidateAuthenticatedContext(state) {
            return {
                ...state,
                loading: true,
                isAuthenticated: false,
                account: undefined,
                sessionHasBeenFetched: false,
                loginError: false,
                errorMessage: undefined,
            };
        },
    },
    extraReducers(builder) {
        builder

            .addCase(authenticate.fulfilled, (state, action) => ({
                ...state,
                loading: false,
                isAuthenticated: true,
                showModalLogin: false,
                loginSuccess: true,
            }))
            .addCase(getAccount.fulfilled, (state, action) => {
                const generationId = action.payload.generationId;
                if (!isActiveSessionGeneration(generationId)) {
                    return state;
                }
                const accountPayload = action.payload.response?.data;
                const isAuthenticated = !!(accountPayload && accountPayload.data);
                return {
                    ...state,
                    isAuthenticated,
                    loading: false,
                    sessionHasBeenFetched: true,
                    account: accountPayload?.data,
                    loginError: false,
                    errorMessage: undefined,
                };
            })
            .addCase(getCustomer.fulfilled, (state, action: PayloadAction<any>) => {
                const isAuthenticated = !!(action?.payload.data)
                return {
                    ...state,
                    isAuthenticated,
                    loading: false,
                    account: action.payload.data.data,
                    customers: action.payload.data.data,
                };
            })
            .addCase(authenticate.pending, state => {
                state.loading = true;
                state.isAuthenticated = false;
            })
            .addCase(getAccount.pending, (state, action) => {
                const generationId = action.meta.arg.generationId;
                if (!isActiveSessionGeneration(generationId)) {
                    return state;
                }
                return {
                    ...state,
                    loading: true,
                    isAuthenticated: false,
                };
            })
            .addCase(authenticate.rejected, (state, action) => ({
                ...initialState,
                isAuthenticated: false,
                errorMessage: action.error.message,
                showModalLogin: true,
                loginError: true,
            }))
            .addCase(getAccount.rejected, (state, action) => {
                const generationId = action.meta.arg?.generationId;
                if (generationId !== undefined && !isActiveSessionGeneration(generationId)) {
                    return state;
                }
                return {
                    ...state,
                    loading: false,
                    loginError: true,
                    errorMessage: action.error.message,
                    isAuthenticated: false,
                    account: undefined,
                    sessionHasBeenFetched: true,
                    showModalLogin: true,
                };
            });
    },
});

export const {logoutSession, clearAuth, authError, invalidateAuthenticatedContext} =
    authenticationReducer.actions;
export const selectAutentication = (state: RootState) => state.authentication;

// Reducer
export default authenticationReducer.reducer;
